import React, { useEffect, useState, useMemo } from "react";
import Layout from "@/components/Layout";
import { api, colones, fmtFecha, todayISO, weekRange, toLocalISO } from "@/lib/api";
import { PlusCircle, Plus, X, Trash2, Pencil, ChevronLeft, ChevronRight, Calendar, Gift, FileDown } from "lucide-react";
import ReportModal from "@/components/ReportModal";
import { reporteBonificaciones } from "@/lib/reportes";
import { empleadosPorTipo, matchesFrecuencia, empleadoActivoEn } from "@/lib/frecuencia";

function getPeriodRange(tipo, offset) {
  if (tipo === "semanal") {
    const r = weekRange(offset);
    return { start: r.start, end: r.end, label: `Semana del ${fmtFecha(r.start)} al ${fmtFecha(r.end)}` };
  }
  const d = new Date();
  d.setMonth(d.getMonth() + Math.floor(offset / 2));
  const half = ((offset % 2) + 2) % 2 === 0;
  const y = d.getFullYear(); const m = d.getMonth();
  let start, end;
  if (half) { start = new Date(y, m, 1); end = new Date(y, m, 15); }
  else { start = new Date(y, m, 16); end = new Date(y, m + 1, 0); }
  return {
    start: toLocalISO(start),
    end: toLocalISO(end),
    label: `${half ? "Primera" : "Segunda"} quincena de ${start.toLocaleDateString("es-CR", { month: "long", year: "numeric" })}`,
  };
}

const BonifModal = ({ bonif, empleados, onClose, onSaved }) => {
  const [data, setData] = useState(bonif || { activo: true, aplica_ccss: false, fecha: todayISO() });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const set = (k, v) => setData(p => ({ ...p, [k]: v }));

  const empleado = empleados.find(e => e.id === data.empleado_id);
  const minFecha = empleado?.fecha_ingreso || undefined;
  const fechaInvalida = !!(empleado && minFecha && data.fecha && data.fecha < minFecha);
  const ccssRebajo = (data.aplica_ccss && empleado?.ccss_aplica && data.monto)
    ? Math.round(Number(data.monto) * 0.1083) : 0;

  const save = async (e) => {
    e.preventDefault();
    if (fechaInvalida) {
      setErr(`La fecha no puede ser anterior al ingreso del empleado (${minFecha}).`);
      return;
    }
    setErr(""); setSaving(true);
    try {
      if (bonif?.id) await api.put(`/bonificaciones/${bonif.id}`, data);
      else await api.post("/bonificaciones", data);
      onSaved();
    } catch (e2) {
      setErr(e2.response?.data?.detail || "Error al guardar la bonificación.");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl fade-in-up" onClick={ev => ev.stopPropagation()} data-testid="bonif-modal">
        <div className="p-6 border-b border-c flex justify-between">
          <h3 className="font-display text-2xl font-semibold flex items-center gap-2"><Gift size={22} className="text-accent-color" /> {bonif ? "Editar bonificación" : "Nueva bonificación"}</h3>
          <button onClick={onClose} className="np-btn np-btn-ghost p-2"><X size={18} /></button>
        </div>
        <form onSubmit={save} className="p-6 space-y-4">
          <div>
            <label className="np-label">Empleado *</label>
            <select required className="np-input" value={data.empleado_id || ""} onChange={e => set("empleado_id", e.target.value)} data-testid="bonif-empleado">
              <option value="">Seleccionar empleado</option>
              {empleados.map(e => <option key={e.id} value={e.id}>{e.nombre} {e.apellidos}{e.ccss_aplica ? "" : " (sin CCSS)"}</option>)}
            </select>
          </div>

          <div>
            <label className="np-label">Fecha de la bonificación *</label>
            <input type="date" required min={minFecha} className="np-input" value={data.fecha || ""} onChange={e => set("fecha", e.target.value)} data-testid="bonif-fecha" />
            {empleado?.fecha_ingreso && (
              <p className="text-[11px] text-secondary-c mt-1">Fecha mínima: {empleado.fecha_ingreso} (ingreso del empleado)</p>
            )}
            {fechaInvalida && <p className="text-xs text-error font-medium mt-1">La fecha es anterior al ingreso del empleado.</p>}
          </div>

          <div>
            <label className="np-label">Monto (₡) *</label>
            <input type="number" required min={0} className="np-input" value={data.monto || ""} onChange={e => set("monto", Number(e.target.value))} data-testid="bonif-monto" />
          </div>

          <div>
            <label className="np-label">Motivo *</label>
            <textarea required rows={2} className="np-input" value={data.motivo || ""} onChange={e => set("motivo", e.target.value)} placeholder="Bono de productividad, cumplimiento de meta, etc." data-testid="bonif-motivo" />
          </div>

          <label className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer ${data.aplica_ccss ? "bg-[#E6EFEA] border-[#C8DACF]" : "border-c bg-[#FAFAF7]"} ${empleado && !empleado.ccss_aplica ? "opacity-60" : ""}`}>
            <input type="checkbox" checked={!!data.aplica_ccss} onChange={e => set("aplica_ccss", e.target.checked)} className="mt-0.5" data-testid="bonif-ccss" disabled={empleado && !empleado.ccss_aplica} />
            <div className="flex-1">
              <div className="font-medium text-sm">Aplicar rebajo CCSS sobre esta bonificación</div>
              <div className="text-xs text-secondary-c mt-0.5">
                {empleado && !empleado.ccss_aplica
                  ? "Este empleado no tiene CCSS activo. La bonificación se paga sin deducción."
                  : "Si está marcado, se descuenta 10,83 % de esta bonificación en la planilla."}
              </div>
              {ccssRebajo > 0 && (
                <div className="text-xs text-error font-medium mt-1">
                  Se descontarán <b>{colones(ccssRebajo)}</b> de CCSS sobre esta bonificación.
                </div>
              )}
            </div>
          </label>

          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={!!data.activo} onChange={e => set("activo", e.target.checked)} />
            <span className="text-sm">Bonificación activa</span>
          </label>
          {err && <div className="p-3 rounded-xl bg-[#FDEDED] text-[#B84B4B] text-sm">{err}</div>}
          <div className="flex justify-end gap-3 pt-2 border-t border-c">
            <button type="button" onClick={onClose} className="np-btn np-btn-ghost">Cancelar</button>
            <button type="submit" disabled={saving || fechaInvalida} className="np-btn np-btn-primary" data-testid="bonif-submit">{saving ? "Guardando..." : (bonif ? "Guardar" : "Crear bonificación")}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default function Bonificaciones() {
  const [tipo, setTipo] = useState("semanal");
  const [offset, setOffset] = useState(0);
  const [items, setItems] = useState([]);
  const [empleados, setEmpleados] = useState([]);
  const [filtroEmp, setFiltroEmp] = useState("");
  const [open, setOpen] = useState(null);
  const [showReport, setShowReport] = useState(false);
  const [empresa, setEmpresa] = useState({});

  useEffect(() => { api.get("/empresa").then(r => setEmpresa(r.data || {})).catch(() => {}); }, []);

  const generarReporte = async ({ format, fechaInicio, fechaFin, periodoLabel }) => {
    const filtrados = items.filter(b => b.fecha && b.fecha >= fechaInicio && b.fecha <= fechaFin);
    reporteBonificaciones({ format, items: filtrados, empleados, periodoLabel, empresa });
  };

  const range = useMemo(() => getPeriodRange(tipo, offset), [tipo, offset]);

  const load = async () => {
    const [b, e] = await Promise.all([api.get("/bonificaciones"), api.get("/empleados")]);
    setItems(b.data); setEmpleados(e.data);
  };
  useEffect(() => { load(); }, []);

  const empById = Object.fromEntries(empleados.map(e => [e.id, e]));
  const empleadosFiltrados = useMemo(
    () => empleadosPorTipo(empleados, tipo).filter(e => empleadoActivoEn(e, range.end)),
    [empleados, tipo, range.end]
  );

  const filtered = useMemo(() => items.filter(b => {
    const emp = empById[b.empleado_id];
    if (!emp || !matchesFrecuencia(emp, tipo)) return false;
    if (filtroEmp && b.empleado_id !== filtroEmp) return false;
    return b.fecha && b.fecha >= range.start && b.fecha <= range.end;
  }), [items, filtroEmp, range, empById, tipo]);

  const totalPeriodo = filtered.filter(b => b.activo).reduce((s, b) => s + (Number(b.monto) || 0), 0);

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar esta bonificación?")) return;
    await api.delete(`/bonificaciones/${id}`);
    load();
  };

  return (
    <Layout>
      <header className="flex items-end justify-between mb-8 flex-wrap gap-4 fade-in-up">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-2xl bg-[#FDF1EC] text-[#D96C4A] flex items-center justify-center"><Gift size={26} strokeWidth={1.5} /></div>
          <div>
            <h1 className="font-display text-3xl font-semibold">Bonificaciones</h1>
            <p className="text-sm text-secondary-c">Bonos, gratificaciones e incentivos por empleado</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowReport(true)} className="np-btn np-btn-outline" data-testid="bonif-report-btn"><FileDown size={16} className="mr-1.5" />Reporte</button>
          <button onClick={() => setOpen("new")} className="np-btn np-btn-primary" data-testid="new-bonif-btn"><Plus size={16} /> Nueva bonificación</button>
        </div>
      </header>

      {showReport && <ReportModal title="Reporte de Bonificaciones" mode="periodo" onClose={() => setShowReport(false)} onGenerate={generarReporte} />}

      <div className="np-card p-4 mb-5 flex flex-wrap items-center gap-4 justify-between">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="inline-flex rounded-xl bg-[#EFEEE8] p-1">
            <button onClick={() => { setTipo("semanal"); setOffset(0); }} className={`px-4 py-1.5 rounded-lg text-sm font-medium ${tipo === "semanal" ? "bg-white text-primary-c shadow-sm" : "text-secondary-c"}`} data-testid="bonif-tab-semanal">Semanal</button>
            <button onClick={() => { setTipo("quincenal"); setOffset(0); }} className={`px-4 py-1.5 rounded-lg text-sm font-medium ${tipo === "quincenal" ? "bg-white text-primary-c shadow-sm" : "text-secondary-c"}`} data-testid="bonif-tab-quincenal">Quincenal</button>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setOffset(o => o - 1)} className="np-btn np-btn-outline p-2" data-testid="bonif-prev"><ChevronLeft size={16} /></button>
            <div className="flex items-center gap-2 px-4 py-2 bg-[#FAFAF7] rounded-xl text-sm font-medium" data-testid="bonif-period-label"><Calendar size={14} /> {range.label}</div>
            <button onClick={() => setOffset(o => o + 1)} className="np-btn np-btn-outline p-2" data-testid="bonif-next"><ChevronRight size={16} /></button>
          </div>
          <select className="np-input w-auto" value={filtroEmp} onChange={e => setFiltroEmp(e.target.value)} data-testid="bonif-filter-empleado">
            <option value="">Todos los empleados</option>
            {empleadosFiltrados.map(e => <option key={e.id} value={e.id}>{e.nombre} {e.apellidos}</option>)}
          </select>
        </div>
        <div className="np-card !p-3 !shadow-none flex items-center gap-3 text-sm">
          <span className="text-secondary-c">Total período:</span>
          <span className="font-semibold text-success font-display text-lg" data-testid="bonif-total">+{colones(totalPeriodo)}</span>
        </div>
      </div>

      <div className="np-card p-0 np-scroll">
        {filtered.length === 0 ? (
          <div className="p-12 text-center"><Gift size={36} className="mx-auto text-secondary-c opacity-40 mb-3" /><div className="font-medium">No hay bonificaciones en este período</div></div>
        ) : (
          <table className="np-table">
            <thead><tr><th>Empleado</th><th>Fecha</th><th>Motivo</th><th>CCSS</th><th>Monto</th><th>Estado</th><th></th></tr></thead>
            <tbody>
              {filtered.map(b => {
                const emp = empById[b.empleado_id];
                return (
                  <tr key={b.id} data-testid={`bonif-row-${b.id}`}>
                    <td className="font-medium">{emp ? `${emp.nombre} ${emp.apellidos}` : "—"}</td>
                    <td className="text-secondary-c text-sm">{fmtFecha(b.fecha)}</td>
                    <td className="text-secondary-c text-sm max-w-[260px]">{b.motivo}</td>
                    <td>
                      {b.aplica_ccss && emp?.ccss_aplica
                        ? <span className="np-badge np-badge-info">Aplica</span>
                        : <span className="np-badge np-badge-neutral">Exento</span>}
                    </td>
                    <td className="font-semibold text-success">+{colones(b.monto)}</td>
                    <td><span className={`np-badge ${b.activo ? "np-badge-success" : "np-badge-neutral"}`}>{b.activo ? "Activo" : "Inactivo"}</span></td>
                    <td>
                      <div className="flex gap-2 justify-end">
                        <button onClick={() => setOpen({ bonif: b })} className="np-btn np-btn-ghost p-2"><Pencil size={15} /></button>
                        <button onClick={() => remove(b.id)} className="np-btn np-btn-ghost p-2 text-error"><Trash2 size={15} /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {open && <BonifModal bonif={open === "new" ? null : open.bonif} empleados={empleadosFiltrados} onClose={() => setOpen(null)} onSaved={() => { setOpen(null); load(); }} />}
    </Layout>
  );
}
